import { __assign, __read, __spreadArray } from "tslib";
import { findIndex, identity, iteratee, xorBy } from 'lodash-es';
import { useCallback, useReducer } from 'react';
export function useMultiSelect(comparator) {
    var TOGGLE_SELECTED = 'TOGGLE_SELECTED';
    var CLEAR_SELECTED = 'CLEAR_SELECTED';
    var ON_SELECTION_START = 'ON_SELECTION_START';
    var ON_SELECTION_END = 'ON_SELECTION_END';
    var SET_SELECTED = 'SET_SELECTED';
    var INCREMENT_SELECTED = 'INCREMENT_SELECTED';
    var _a = __read(useReducer(function (prevState, _a) {
        var type = _a.type, payload = _a.payload;
        switch (type) {
            case TOGGLE_SELECTED: {
                var selected = xorBy(prevState.selected, toArray(payload), comparator);
                return __assign(__assign({}, prevState), { selected: selected, incrementalSelectOriginItem: getIncrementalSelectOriginItem({
                        currentOrigin: prevState.incrementalSelectOriginItem,
                        selectedItems: selected,
                        toggledItems: toArray(payload),
                        comparator: comparator,
                    }) });
            }
            case CLEAR_SELECTED:
                return __assign(__assign({}, prevState), { selected: [], incrementalSelectOriginItem: null });
            case SET_SELECTED:
                return __assign(__assign({}, prevState), { selected: toArray(payload), incrementalSelectOriginItem: null });
            case INCREMENT_SELECTED: {
                var allItems = payload.allItems, item = payload.item;
                return __assign(__assign({}, prevState), { selected: incrementSelection({
                        allItems: allItems,
                        selectOriginItem: prevState.incrementalSelectOriginItem,
                        newlySelectedItem: item,
                        comparator: comparator,
                    }) });
            }
            case ON_SELECTION_START: {
                var shiftKey = payload.shiftKey;
                return {
                    isSelecting: true,
                    selected: shiftKey ? prevState.selected : [],
                };
            }
            case ON_SELECTION_END: {
                var event = payload.event, items = payload.items;
                return {
                    isSelecting: false,
                    selected: event.shiftKey
                        ? xorBy(prevState.selected, toArray(items), comparator)
                        : items,
                };
            }
            default:
                throw new Error("Unrecognised action: ".concat(type));
        }
    }, {
        selected: [],
        isSelecting: false,
        incrementalSelectOriginItem: null,
    }), 2), state = _a[0], dispatch = _a[1];
    var toggleSelected = useBoundDispatch(TOGGLE_SELECTED);
    var clearSelected = useBoundDispatch(CLEAR_SELECTED);
    var onSelectionStart = useBoundDispatch(ON_SELECTION_START);
    var setSelected = useBoundDispatch(SET_SELECTED);
    var mapOnSelectionEndPayload = useCallback(function (items, event) {
        return { items: items, event: event };
    }, []);
    var onSelectionEnd = useBoundDispatch(ON_SELECTION_END, mapOnSelectionEndPayload);
    var mapIncrementSelectedPayload = useCallback(function (allItems, item) {
        return { allItems: allItems, item: item };
    }, []);
    var incrementSelected = useBoundDispatch(INCREMENT_SELECTED, mapIncrementSelectedPayload);
    function useBoundDispatch(type, mapPayload) {
        if (mapPayload === void 0) { mapPayload = identity; }
        return useCallback(function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            dispatch({ type: type, payload: mapPayload.apply(void 0, __spreadArray([], __read(args), false)) });
        }, [type, mapPayload]);
    }
    return __assign(__assign({}, state), { toggleSelected: toggleSelected, clearSelected: clearSelected, onSelectionStart: onSelectionStart, onSelectionEnd: onSelectionEnd, setSelected: setSelected, incrementSelected: incrementSelected });
}
function incrementSelection(_a) {
    var allItems = _a.allItems, selectOriginItem = _a.selectOriginItem, newlySelectedItem = _a.newlySelectedItem, comparator = _a.comparator;
    // Increment selection (typically enabled by shift+select in a list view)
    if (!selectOriginItem) {
        return [newlySelectedItem];
    }
    var transform = iteratee(comparator);
    var newSelectionIndex = allItems.findIndex(function (item) { return transform(item) === transform(newlySelectedItem); });
    var selectOriginIndex = allItems.findIndex(function (item) { return transform(item) === transform(selectOriginItem); });
    if (newSelectionIndex < selectOriginIndex) {
        return allItems.slice(newSelectionIndex, selectOriginIndex + 1);
    }
    return allItems.slice(selectOriginIndex, newSelectionIndex + 1);
}
function getIncrementalSelectOriginItem(_a) {
    var currentOrigin = _a.currentOrigin, selectedItems = _a.selectedItems, toggledItems = _a.toggledItems, comparator = _a.comparator;
    // TODO what to do when multiple items toggled?
    var toggledItem = toggledItems[0];
    var transform = iteratee(comparator);
    var toggledItemIndex = findIndex(selectedItems, function (item) { return transform(item) === transform(toggledItem); });
    return toggledItemIndex !== -1 ? toggledItem : currentOrigin;
}
function toArray(items) {
    return Array.isArray(items) ? items : [items];
}
